import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import PrintReceiptModal from '@components/OrderController/Modals/PrintReceipt';
import PrintReceiptLoadingModal from '@components/OrderController/Modals/PrintReceiptLoading';
import FullWithModal from '@components/common/Modal/FullWidthModal';
import { TouchableButton } from '@components/common/Touchable';
import EventEmitter from '@utility/eventSubscribers';
import { CUSTOM_MESSAGES } from '@veriables';
import { printing } from '@reducers/storeChecks';
import {
  setErrorPopUp,
  setNotification,
} from '@reducers/notification';

const SUCCESS_DELAY_MS = 5000;

const PrintPopup = ({ row, serverAppTheme, onClose }) => {
  const dispatch = useDispatch();

  const [seatData, setSeatData] = useState([]);
  const [printRequested, setPrintRequested] = useState(false);
  const [showPrinterLoader, setShowPrinterLoader] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printSuccess, setPrintSuccess] = useState(false);

  const deviceInfo = useSelector(s => s.oauth.deviceInformation);
  const userInfo = useSelector(s => s.oauth.loggedInUserData);

  const successTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    },
    []
  );

  const printListener = useCallback(
    data => {
      if (data?.type === 'error')
        dispatch(setNotification({ res: data.message }));
    },
    [dispatch]
  );

  const print = useCallback(
    seatNumbers => {
      const newArray = [...new Set(seatNumbers)];
      const seatIds = [];
      if (!newArray.includes(-1)) {
        newArray.forEach(num => {
          row?.seats?.forEach(seat => {
            if (seat.open && seat.number === num) seatIds.push(seat.id);
          });
        });
      }
      const dataToSend = {
        deviceId: deviceInfo?.deviceId,
        employeeId: Number(userInfo?.id),
        employeeCard: userInfo?.employeeCard,
        storeId: deviceInfo?.storeId,
        checkHeaderId: row?.id,
        seatIds,
      };

      setPrintRequested(true);

      dispatch(printing(dataToSend))
        .then(res => {
          setPrintData(res);
          setShowPrinterLoader(true);

          if (successTimerRef.current) clearTimeout(successTimerRef.current);
          successTimerRef.current = setTimeout(() => {
            setShowPrinterLoader(false);
            setPrintSuccess(true);
            successTimerRef.current = null;
          }, SUCCESS_DELAY_MS);

          try {
            if (window.ReactNativeWebView && !res?.printerAgentBased) {
              EventEmitter.print(res);
              EventEmitter.printData(printListener, err =>
                dispatch(setErrorPopUp({ res: err?.message || err }))
              );
            }
          } catch (emitErr) {
            console.log('EventEmitter print error:', emitErr);
          }
        })
        .catch(e => {
          setPrintRequested(false);
          dispatch(setErrorPopUp({ res: e?.message || e }));
        });
    },
    [dispatch, row, deviceInfo, userInfo, printListener]
  );

  const setIsPrintSelect = useCallback(
    update => {
      const next =
        typeof update === 'function'
          ? update({ isOpen: true, data: seatData })
          : update;
      if (next?.isOpen === false) {
        if (!printRequested) onClose();
        return;
      }
      setSeatData(next?.data || []);
    },
    [seatData, onClose, printRequested]
  );

  const onCloseSuccess = useCallback(() => {
    setPrintSuccess(false);
    onClose();
  }, [onClose]);

  const showSeatSelector = !printRequested;

  const successStyle = useMemo(
    () => ({
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      width: '486px',
      padding: '1.5rem',
      pointerEvents: 'auto',
      backgroundClip: 'padding-box',
      height: '216px',
      borderRadius: '16px',
      backgroundColor: serverAppTheme?.popUp_bg,
      border: serverAppTheme?.offers_Border,
      boxShadow: serverAppTheme?.popUp_box_shadow,
    }),
    [serverAppTheme]
  );

  return (
    <>
      {showSeatSelector && (
        <PrintReceiptModal
          isPrintSelect={{ isOpen: true, data: seatData }}
          serverAppTheme={serverAppTheme}
          setIsPrintSelect={setIsPrintSelect}
          check={row}
          print={print}
          isOrderingPortrait={false}
        />
      )}
      {showPrinterLoader && (
        <PrintReceiptLoadingModal
          isPrintOpen={showPrinterLoader}
          printData={printData}
          serverAppTheme={serverAppTheme}
        />
      )}
      {printSuccess && (
        <FullWithModal
          isOpen={printSuccess}
          customClass2={cn(
            'w-100 h-100 p-3 d-flex justify-content-center align-items-center'
          )}
          style={successStyle}
        >
          <div className="col-12 fs_18 fw-400 d-flex justify-content-center align-items-center text-center">
            <div>
              <div
                className="text-center pb-25 mb-10"
                style={{ color: serverAppTheme?.primary_text }}
              >
                {CUSTOM_MESSAGES().PRINT_SUCCESS}
              </div>
            </div>
            <div
              className="col-12 px-0 mt-2 position-absolute"
              style={{ bottom: 0, left: 0 }}
            >
              <TouchableButton
                className="col-12 px-0 btn44 btn-ripple fs-24"
                style={{
                  background: serverAppTheme?.secondaryBtn,
                  color: serverAppTheme?.btn_font_color,
                }}
                onClick={onCloseSuccess}
              >
                {CUSTOM_MESSAGES().PRINT_CLOSE}
              </TouchableButton>
            </div>
          </div>
        </FullWithModal>
      )}
    </>
  );
};

export default PrintPopup;
